import { useEffect, useRef, useState } from "react";

//  # Why not use `window.onbeforeunload`?
//  `onbeforeunload` only triggers when the page is about to unload completely
//  (closing tab, refreshing, navigating to external URL).
//  It does NOT trigger for SPA (Single Page Application) navigation like
//  browser back/forward buttons within the same app.
//  React Router navigation bypasses `onbeforeunload` entirely.

export const useNavigationGuard = ({
  shouldGuard,
  message = "Are you sure you want to leave? Your changes will be lost.",
  navigate = null,
  onClose = null,
  interceptClicks = true
}) => {
  const shouldGuardRef = useRef(shouldGuard);
  const isNavigatingRef = useRef(false);
  const skipBeforeUnloadRef = useRef(false);
  const pendingNavigationRef = useRef(null);
  const pushedStateRef = useRef(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  shouldGuardRef.current = shouldGuard;

  useEffect(() => {
    const handlePopState = () => {
      if (!shouldGuardRef.current) return;

      if (isNavigatingRef.current) {
        isNavigatingRef.current = false;
        pushedStateRef.current = false;
        return;
      }

      // Show modal instead of window.confirm
      pendingNavigationRef.current = "back";
      setShowConfirmModal(true);
      
      // Push forward to cancel the back navigation
      window.history.pushState(null, "", window.location.href);
      pushedStateRef.current = true;
    };

    // Push initial state to intercept back button
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // link/button clicks for sidebar and other navigation
  useEffect(() => {
    if (!shouldGuard || !interceptClicks || showConfirmModal) return;

    const handleClick = (e) => {
      if (!shouldGuardRef.current || isNavigatingRef.current) return;

      const target = e.target.closest('a, button, [role="button"]');
      if (!target) return;

      const href = target.getAttribute('href');
      const dataPath = target.getAttribute('data-path');
      const dataAction = target.getAttribute('data-action');

      // Only intercept navigation elements
      const isNavigationElement = href || dataPath || dataAction;
      if (!isNavigationElement) return;

      // Skip special links
      if (href === '#' || href?.startsWith('mailto:') || href?.startsWith('tel:')) return;

      e.preventDefault();
      e.stopPropagation();

      if (dataAction === 'action') {
        pendingNavigationRef.current = { type: 'reclick', element: target };
      } else {
        pendingNavigationRef.current = href || dataPath;
      }

      setShowConfirmModal(true);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [shouldGuard, interceptClicks, showConfirmModal]);

  // Handle tab close or refresh
  useEffect(() => {
    if (!shouldGuard) return;

    const handleBeforeUnload = (e) => {
      if (skipBeforeUnloadRef.current) {
        skipBeforeUnloadRef.current = false;
        return;
      }
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldGuard]);

  const handleConfirm = () => {
    skipBeforeUnloadRef.current = true;
    isNavigatingRef.current = true;
    setShowConfirmModal(false);

    if (onClose) onClose();

    const pending = pendingNavigationRef.current;

    // Handle browser back button
    if (pending === "back") {
      const steps = pushedStateRef.current ? -2 : -1;
      navigate ? navigate(steps) : window.history.go(steps);
    }
    // Handle action buttons (logout, etc.)
    else if (pending?.type === 'reclick') {
      setTimeout(() => pending.element.click(), 0);
    }
    // Handle navigation to a path
    else if (pending) {
      navigate ? navigate(pending) : (window.location.href = pending);
    }

    // Cleanup
    setTimeout(() => { isNavigatingRef.current = false; }, 100);
    pendingNavigationRef.current = null;
    pushedStateRef.current = false;
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    pendingNavigationRef.current = null;
    pushedStateRef.current = false;
  };

  return {
    showConfirmModal,
    message,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
  };
};
